import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Building2, Globe2, Key, Plus, Shield, Trash2, UserCog, Users } from 'lucide-react';

const roleOptions = [
  { value: 'administrator', label: 'Administrator', description: 'Kelola seluruh sistem tanpa batas.' },
  { value: 'admin_finance', label: 'Admin Finance', description: 'Kelola transaksi pemasukan dan pengeluaran sesuai akses cabang.' },
  { value: 'staff_finance', label: 'Staff Finance', description: 'Hanya baca data keuangan sesuai akses cabang.' },
];

const roleBadgeClass = {
  administrator: 'bg-violet-100 text-violet-700',
  admin_finance: 'bg-emerald-100 text-emerald-700',
  staff_finance: 'bg-blue-100 text-blue-700',
};

const roleLabelMap = Object.fromEntries(roleOptions.map((item) => [item.value, item.label]));

const emptyForm = {
  username: '',
  nama: '',
  password: '',
  role: 'staff_finance',
  global_access: false,
  assigned_cabang: [],
};

const UserManagement = () => {
  const { token, isAdmin } = useAuth();
  const { cabang } = useApp();
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(emptyForm);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
    } catch (error) {
      toast.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, token]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingUser(null);
  };

  const handleRoleChange = (role) => {
    setFormData((prev) => ({
      ...prev,
      role,
      global_access: role === 'administrator' ? true : prev.global_access,
      assigned_cabang: role === 'administrator' ? [] : prev.assigned_cabang,
    }));
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        nama: user.nama,
        password: '',
        role: user.role,
        global_access: !!user.global_access,
        assigned_cabang: user.assigned_cabang || [],
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        nama: formData.nama,
        role: formData.role,
        global_access: formData.role === 'administrator' ? true : !!formData.global_access,
        assigned_cabang: formData.role === 'administrator' || formData.global_access ? [] : formData.assigned_cabang,
      };

      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('User berhasil diperbarui');
      } else {
        if (!formData.username || !formData.password) {
          toast.error('Username dan password wajib diisi');
          return;
        }
        await api.post(
          '/users',
          {
            ...payload,
            username: formData.username,
            password: formData.password,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success('User berhasil ditambahkan');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Yakin ingin menghapus user ini?')) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User berhasil dihapus');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Generate password sementara yang aman untuk user ini?')) {
      return;
    }

    try {
      const res = await api.post(`/users/${userId}/reset-password`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const temporaryPassword = res.data?.temporary_password;
      if (temporaryPassword) {
        window.prompt(`Password sementara untuk ${res.data?.username || 'user'}.\nSimpan sekarang karena hanya tampil sekali:`, temporaryPassword);
      }
      toast.success('Password sementara berhasil dibuat');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reset password');
    }
  };

  const toggleCabang = (cabangId) => {
    setFormData((prev) => {
      const current = prev.assigned_cabang || [];
      if (current.includes(cabangId)) {
        return { ...prev, assigned_cabang: current.filter((id) => id !== cabangId) };
      }
      return { ...prev, assigned_cabang: [...current, cabangId] };
    });
  };

  const getCabangNames = (cabangIds) => {
    if (!cabangIds || cabangIds.length === 0) return '-';
    return cabangIds
      .map((id) => {
        const found = cabang.find((item) => item.id === id);
        return found ? found.nama : id;
      })
      .join(', ');
  };

  const getAccessLabel = (user) => {
    if (user.role === 'administrator') return 'Semua Sistem';
    if (user.global_access) return 'Semua Cabang';
    return getCabangNames(user.assigned_cabang);
  };

  if (!isAdmin) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Akses ditolak. Hanya administrator yang bisa mengakses halaman ini.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">User Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">Kelola role, akses global, dan cabang untuk setiap user.</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
          <Plus className="mr-2 h-4 w-4" />
          Tambah User
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-violet-200 bg-violet-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-violet-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-violet-700">Total User</p>
                <p className="text-2xl font-black text-violet-600">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-purple-700">Administrator</p>
                <p className="text-2xl font-black text-purple-600">{users.filter((item) => item.role === 'administrator').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <UserCog className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-emerald-700">Admin Finance</p>
                <p className="text-2xl font-black text-emerald-600">{users.filter((item) => item.role === 'admin_finance').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-blue-700">Staff Finance</p>
                <p className="text-2xl font-black text-blue-600">{users.filter((item) => item.role === 'staff_finance').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border/60 bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="w-[50px]">No</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Nama</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Akses</TableHead>
                  <TableHead className="w-[160px] text-center">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      Memuat...
                    </TableCell>
                  </TableRow>
                ) : users.length > 0 ? (
                  users.map((user, index) => (
                    <TableRow key={user.id} className="hover:bg-zinc-50/50">
                      <TableCell className="font-mono text-muted-foreground">{index + 1}</TableCell>
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>{user.nama}</TableCell>
                      <TableCell>
                        <span className={`rounded px-2 py-1 text-xs ${roleBadgeClass[user.role] || 'bg-zinc-100 text-zinc-700'}`}>
                          {roleLabelMap[user.role] || user.role}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[260px] text-sm text-muted-foreground">{getAccessLabel(user)}</TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleResetPassword(user.id)} title="Reset Password" data-testid={`reset-password-${user.id}`}>
                            <Key className="h-4 w-4 text-amber-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(user)} data-testid={`edit-user-${user.id}`}>
                            <UserCog className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} className="text-rose-500 hover:text-rose-600" data-testid={`delete-user-${user.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      Belum ada user
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editingUser ? 'Edit User' : 'Tambah User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Perbarui role dan akses user.' : 'Buat user baru dan tentukan hak aksesnya.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!editingUser && (
              <>
                <div className="space-y-2">
                  <Label>Username <span className="text-rose-500">*</span></Label>
                  <Input
                    value={formData.username}
                    onChange={(event) => setFormData({ ...formData, username: event.target.value })}
                    placeholder="Username untuk login"
                    data-testid="input-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password <span className="text-rose-500">*</span></Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(event) => setFormData({ ...formData, password: event.target.value })}
                    placeholder="Password awal"
                    data-testid="input-password"
                  />
                  <p className="text-xs text-muted-foreground">Minimal 12 karakter dengan kombinasi huruf besar, huruf kecil, angka, atau simbol.</p>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Nama Lengkap</Label>
              <Input
                value={formData.nama}
                onChange={(event) => setFormData({ ...formData, nama: event.target.value })}
                placeholder="Nama lengkap user"
                data-testid="input-nama-user"
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={handleRoleChange}>
                <SelectTrigger data-testid="select-role">
                  <SelectValue placeholder="Pilih Role" />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {roleOptions.find((item) => item.value === formData.role)?.description}
              </p>
            </div>

            {formData.role !== 'administrator' && (
              <>
                <div className="flex items-center space-x-2 rounded-lg border border-border/60 bg-zinc-50 px-4 py-3">
                  <Checkbox
                    id="global-access"
                    checked={formData.global_access}
                    onCheckedChange={(checked) => setFormData({ ...formData, global_access: !!checked, assigned_cabang: checked ? [] : formData.assigned_cabang })}
                  />
                  <Label htmlFor="global-access" className="flex cursor-pointer items-center gap-2">
                    <Globe2 className="h-4 w-4 text-blue-500" />
                    Akses semua cabang
                  </Label>
                </div>

                {!formData.global_access && (
                  <div className="space-y-2">
                    <Label>Cabang yang Bisa Diakses</Label>
                    <div className="max-h-[220px] space-y-2 overflow-y-auto rounded-sm border p-3">
                      {cabang.length > 0 ? (
                        cabang.map((item) => (
                          <div key={item.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`cabang-${item.id}`}
                              checked={(formData.assigned_cabang || []).includes(item.id)}
                              onCheckedChange={() => toggleCabang(item.id)}
                            />
                            <Label htmlFor={`cabang-${item.id}`} className="cursor-pointer text-sm font-normal">
                              {item.nama}
                            </Label>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">Belum ada cabang. Tambah cabang terlebih dahulu.</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleSubmit} data-testid="submit-user-btn">
              {editingUser ? 'Simpan' : 'Tambah'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
